(() => {
  if (!customElements.get('caffrey-suede-product')) {
    customElements.define('caffrey-suede-product', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const signal = this.controller.signal;
        this.form = this.querySelector('form');
        this.busy = false;
        this.past = false;
        this.atFooter = false;
        this.scheduleSticky = () => {
          cancelAnimationFrame(this.stickyFrame);
          this.stickyFrame = requestAnimationFrame(() => this.sticky());
        };
        window.addEventListener('scroll', this.scheduleSticky, { passive: true, signal });
        window.addEventListener('resize', this.scheduleSticky, { passive: true, signal });
        this.form?.addEventListener('submit', e => this.add(e), { signal });
        this.querySelector('[data-su-variant]')?.addEventListener('change', () => this.sync(true), { signal });
        const dialog = this.querySelector('dialog');
        const trigger = this.querySelector('[data-su-zoom]');
        trigger?.addEventListener('click', e => {
          if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          dialog.showModal();
        }, { signal });
        dialog?.querySelector('[data-su-close]')?.addEventListener('click', () => dialog.close(), { signal });
        dialog?.addEventListener('click', e => { if (e.target === dialog) dialog.close(); }, { signal });
        dialog?.addEventListener('close', () => trigger.focus({ preventScroll: true }), { signal });
        this.buttonObserver?.disconnect();
        this.buttonObserver = new IntersectionObserver(([entry]) => {
          this.past = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          this.sticky();
        });
        const button = this.querySelector('.cf-su-main-add');
        if (button) this.buttonObserver.observe(button);
        this.footerObserver?.disconnect();
        this.footerObserver = new IntersectionObserver(([entry]) => {
          this.atFooter = entry.isIntersecting || entry.boundingClientRect.bottom < 0;
          this.sticky();
        });
        const footer = document.querySelector('footer');
        if (footer) this.footerObserver.observe(footer);
        this.sync();
        this.sticky();
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.buttonObserver?.disconnect();
        this.footerObserver?.disconnect();
        cancelAnimationFrame(this.stickyFrame);
      }
      selected() {
        const input = this.querySelector('[data-su-variant]');
        return input?.tagName === 'SELECT' ? input.selectedOptions[0] : input;
      }
      sync(url = false) {
        const option = this.selected();
        if (!option) return;
        this.querySelectorAll('[data-su-price]').forEach(e => e.textContent = option.dataset.price);
        this.querySelectorAll('[data-su-add]').forEach(button => {
          button.disabled = this.busy || option.dataset.available !== 'true';
          button.setAttribute('aria-busy', String(this.busy));
          button.textContent = this.busy ? this.dataset.adding : option.dataset.available === 'true' ? this.dataset.add : this.dataset.soldOut;
        });
        if (url) {
          const next = new URL(location.href);
          next.searchParams.set('variant', option.value);
          history.replaceState({}, '', next);
        }
      }
      sticky() {
        const bar = this.querySelector('[data-su-sticky]');
        const button = this.querySelector('.cf-su-main-add');
        const footer = document.querySelector('footer');
        const past = button && button.getBoundingClientRect().bottom < 0;
        const atFooter = footer && footer.getBoundingClientRect().top < innerHeight;
        if (bar) bar.hidden = !past || atFooter;
      }
      feedback(message, error = false, link = false) {
        const output = this.querySelector('[data-su-feedback]');
        output.replaceChildren(document.createTextNode(message));
        output.dataset.error = String(error);
        if (link) {
          const a = document.createElement('a');
          a.href = window.routes?.cart_url || '/cart';
          a.textContent = this.dataset.viewCart;
          output.append(a);
        }
      }
      async add(event) {
        event.preventDefault();
        if (this.busy || !this.form.reportValidity() || this.selected()?.dataset.available !== 'true') return;
        this.busy = true;
        this.sync();
        this.feedback('');
        let sent = false;
        try {
          const data = new FormData(this.form);
          const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
          if (cart?.getSectionsToRender) {
            data.set('sections', cart.getSectionsToRender().map(section => section.id).join(','));
            data.set('sections_url', location.pathname + location.search);
            cart.setActiveElement?.(document.activeElement);
          }
          sent = true;
          const result = await fetch((window.routes?.cart_add_url || '/cart/add') + '.js', {
            method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: data
          });
          const response = await result.json();
          if (!result.ok || response.status) {
            this.feedback(response.description || this.dataset.error, true);
            return;
          }
          this.feedback(this.dataset.added, false, true);
          if (cart?.renderContents && response.sections) {
            cart.classList.remove('is-empty');
            cart.renderContents(response);
          } else location.assign(window.routes?.cart_url || '/cart');
        } catch {
          this.feedback(sent ? this.dataset.checkCart : this.dataset.error, true, sent);
        } finally {
          this.busy = false;
          if (this.isConnected) this.sync();
        }
      }
    });
  }
  if (!customElements.get('caffrey-suede-effect')) {
    customElements.define('caffrey-suede-effect', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const signal = this.controller.signal;
        this.picture = this.querySelector('img');
        this.canvas = this.querySelector('canvas');
        this.context = this.canvas?.getContext('2d');
        this.button = this.querySelector('[data-su-effect-control]');
        if (!this.picture || !this.context || !this.button) return;
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.progress = 0;
        this.running = false;
        this.played = false;
        this.visible = false;
        this.ready = false;
        this.frame = 0;
        this.button.addEventListener('click', () => this.running ? this.pause() : this.play(this.progress >= 1), { signal });
        this.motion.addEventListener('change', () => this.preferences(), { signal });
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.suspend();
          else if (this.visible && this.resumeOnView) this.play();
        }, { signal });
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting && entry.intersectionRatio >= .6;
          if (!this.visible) this.suspend();
          else if (this.ready && (!this.played || this.resumeOnView)) this.play();
        }, { threshold: .6 });
        this.visibilityObserver.observe(this.querySelector('.cf-su-effect-visual'));
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(this.querySelector('.cf-su-effect-visual'));
        const load = () => {
          if (!this.isConnected || signal.aborted || !this.picture.naturalWidth) return;
          this.ready = true;
          this.resize();
          this.classList.add('is-ready');
          this.preferences();
          if (this.visible && !this.motion.matches) this.play();
        };
        if (this.picture.complete && this.picture.naturalWidth) load();
        else this.picture.addEventListener('load', load, { once: true, signal });
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.visibilityObserver?.disconnect();
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.frame);
        this.running = false;
      }
      resize() {
        if (!this.ready) return;
        const bounds = this.querySelector('.cf-su-effect-visual').getBoundingClientRect();
        const width = Math.round(bounds.width), height = Math.round(bounds.height);
        if (!width || !height) return;
        this.width = width;
        this.height = height;
        const scale = Math.min(devicePixelRatio || 1, 1.5, 1200 / width);
        this.canvas.width = Math.round(width * scale);
        this.canvas.height = Math.round(height * scale);
        this.context.setTransform(scale, 0, 0, scale, 0, 0);
        this.draw(this.motion.matches ? .4 : this.progress);
      }
      preferences() {
        this.button.hidden = this.motion.matches;
        if (this.motion.matches) {
          this.pause();
          this.draw(.4);
        } else {
          this.draw(this.progress);
          this.controls();
        }
      }
      controls() {
        const text = this.running ? this.dataset.pause : this.progress >= 1 ? this.dataset.replay : this.dataset.play;
        this.button.querySelector('[data-su-effect-label]').textContent = text;
        this.button.querySelector('[data-su-effect-icon]').textContent = this.running ? 'Ⅱ' : '▷';
        this.button.setAttribute('aria-label', text + ': ' + (this.dataset.title || this.querySelector('h2')?.textContent.trim() || ''));
        this.dataset.state = this.running ? 'playing' : this.progress >= 1 ? 'complete' : 'paused';
      }
      play(restart = false) {
        if (!this.ready || this.motion.matches || document.hidden) return;
        cancelAnimationFrame(this.frame);
        if (restart) this.progress = 0;
        this.running = true;
        this.played = true;
        this.resumeOnView = false;
        this.start = performance.now() - this.progress * 4400;
        this.lastDraw = 0;
        this.controls();
        const tick = now => {
          if (!this.running || !this.isConnected) return;
          this.progress = Math.min(1, (now - this.start) / 4400);
          if (now - this.lastDraw >= 32 || this.progress === 1) {
            this.draw(this.progress);
            this.lastDraw = now;
          }
          if (this.progress < 1) this.frame = requestAnimationFrame(tick);
          else { this.running = false; this.frame = 0; this.controls(); }
        };
        this.frame = requestAnimationFrame(tick);
      }
      pause() {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.running = false;
        this.resumeOnView = false;
        this.controls();
      }
      suspend() {
        if (!this.running) return;
        this.pause();
        this.resumeOnView = true;
      }
      draw(progress) {
        if (!this.ready || !this.width) return;
        const ctx = this.context, w = this.width, h = this.height, picture = this.picture;
        const imageWidth = w * 1.13;
        const bleed = (imageWidth - w) / 2;
        const imageHeight = imageWidth * picture.naturalHeight / picture.naturalWidth;
        const crop = (imageHeight - h) / 2;
        const sourceScale = picture.naturalWidth / imageWidth;
        ctx.clearRect(0, 0, w, h);
        if (this.dataset.effect === 'condition') {
          const wave = Math.sin(progress * Math.PI * 4) * Math.sin(progress * Math.PI);
          const row = 2;
          for (let y = 0; y < h; y += row) {
            const thickness = Math.min(row, h - y);
            const depth = (y + crop) / imageHeight;
            const flexibility = Math.max(0, Math.min(1, (.73 - depth) / .34));
            const shift = wave * w * .055 * flexibility * (.82 + Math.sin(depth * 12) * .18);
            ctx.drawImage(picture, 0, (y + crop) * sourceScale, picture.naturalWidth, thickness * sourceScale, shift - bleed, y, imageWidth, thickness);
          }
        } else {
          ctx.drawImage(picture, bleed * sourceScale, crop * sourceScale, w * sourceScale, h * sourceScale, 0, 0, w, h);
          for (let i = 0; i < 32; i++) {
            const seed = Math.sin((i + 1) * 78.233) * 43758.5453;
            const random = seed - Math.floor(seed);
            const x = w * (.13 + (i % 11) * .071 + random * .035);
            const origin = imageHeight * (.49 + random * .14) - crop;
            const lift = Math.max(0, Math.min(1, (progress - .1 - (i % 7) * .025) / .72));
            const eased = lift * lift;
            const px = x + Math.sin(lift * 4 + i) * eased * w * .025;
            const py = origin - eased * h * (.38 + random * .12);
            const radius = Math.max(2.2, w * (.006 + random * .006));
            ctx.globalAlpha = Math.max(0, 1 - Math.max(0, lift - .65) / .35);
            ctx.fillStyle = i % 3 ? '#4b423c' : '#86796d';
            ctx.beginPath();
            for (let point = 0; point < 6; point++) {
              const angle = point / 6 * Math.PI * 2;
              const r = radius * (point % 2 ? .65 : 1);
              const dx = px + Math.cos(angle) * r, dy = py + Math.sin(angle) * r;
              if (point) ctx.lineTo(dx, dy); else ctx.moveTo(dx, dy);
            }
            ctx.closePath(); ctx.fill();
          }
          ctx.globalAlpha = 1;
        }
        this.dataset.progress = progress.toFixed(3);
      }
    });
  }
})();
